import {
  TABLE_WATERMARK_ATTR,
  TABLE_WATERMARK_LAYER_ATTR,
  TABLE_WATERMARK_LOGO_ATTR,
  TABLE_WATERMARK_OFFSET_ATTR,
  TABLE_WATERMARK_MOBILE_MEDIA,
  getWatermarkLayerLayoutStyle,
} from 'styles/tableWatermark';
import { TABLE_WATERMARK_ENABLED } from 'constants/tableWatermark';
import {
  clearWatermarkContainerConfig,
  setWatermarkContainerConfig,
} from 'utils/tableWatermarkConfig';
import {
  getWatermarkOffsetTopFromConfig,
  syncWatermarkLogos,
} from 'utils/tableWatermarkLayout';

const registeredContainers = new Set();
const restoringNodes = new WeakSet();
const resizeObservers = new WeakMap();
const pendingEnsureNodes = new Set();

let observer = null;
let observerOptions = null;
let mediaQueryList = null;
let syncFrame = null;
let listenersStarted = false;

const normalizeInset = value => {
  if (!value) {
    return '0|0|0|0';
  }

  const parts = value.trim().split(/\s+/);

  while (parts.length < 4) {
    parts.push('0');
  }

  return parts
    .slice(0, 4)
    .map(part => {
      if (part === '0') {
        return '0';
      }

      const parsed = parseFloat(part);

      return Number.isNaN(parsed) ? part : String(parsed);
    })
    .join('|');
};

const isStyleValueEqual = (key, current, expected) => {
  if (key === 'inset') {
    return normalizeInset(current) === normalizeInset(expected);
  }

  return String(current || '') === String(expected || '');
};

const isLayerValid = (layer, offsetTop) =>
  Object.entries(
    getWatermarkLayerLayoutStyle(offsetTop)
  ).every(([key, value]) => isStyleValueEqual(key, layer.style[key], value));

const isContainerValid = node =>
  node.getAttribute(TABLE_WATERMARK_ATTR) === 'true';

const applyStyleIfChanged = (layer, key, value) => {
  if (!isStyleValueEqual(key, layer.style[key], value)) {
    // eslint-disable-next-line no-param-reassign
    layer.style[key] = value;
  }
};

const restoreLayerStyles = (layer, offsetTop) => {
  Object.entries(getWatermarkLayerLayoutStyle(offsetTop)).forEach(
    ([key, value]) => {
      applyStyleIfChanged(layer, key, value);
    }
  );
};

const createWatermarkLayer = offsetTop => {
  const layer = document.createElement('div');
  layer.setAttribute(TABLE_WATERMARK_LAYER_ATTR, 'true');
  layer.setAttribute('aria-hidden', 'true');
  restoreLayerStyles(layer, offsetTop);

  return layer;
};

const pauseObserver = () => {
  if (observer) {
    observer.disconnect();
  }
};

const resumeObserver = () => {
  if (!observer || !observerOptions) {
    return;
  }

  registeredContainers.forEach(node => {
    if (node && node.isConnected) {
      observer.observe(node, observerOptions);
    }
  });
};

const ensureWatermark = node => {
  if (!node || !node.isConnected || restoringNodes.has(node)) {
    return null;
  }

  const offsetTop = getWatermarkOffsetTopFromConfig(node);
  const existingLayer = node.querySelector(`[${TABLE_WATERMARK_LAYER_ATTR}]`);

  restoringNodes.add(node);
  pauseObserver();

  try {
    if (!isContainerValid(node)) {
      node.setAttribute(TABLE_WATERMARK_ATTR, 'true');
    }

    if (node.getAttribute(TABLE_WATERMARK_OFFSET_ATTR) !== String(offsetTop)) {
      node.setAttribute(TABLE_WATERMARK_OFFSET_ATTR, String(offsetTop));
    }

    if (node.hasAttribute('data-table-watermark-left')) {
      node.removeAttribute('data-table-watermark-left');
    }

    if (node.hasAttribute('data-table-watermark-left-mobile')) {
      node.removeAttribute('data-table-watermark-left-mobile');
    }

    let layer = existingLayer;

    if (!layer) {
      layer = createWatermarkLayer(offsetTop);
      node.appendChild(layer);
    } else if (!isLayerValid(layer, offsetTop)) {
      restoreLayerStyles(layer, offsetTop);
    }

    applyStyleIfChanged(layer, 'opacity', '1');
    applyStyleIfChanged(layer, 'visibility', 'visible');
    applyStyleIfChanged(layer, 'display', 'block');
    applyStyleIfChanged(layer, 'filter', 'none');

    syncWatermarkLogos(node, layer);

    return layer;
  } catch (error) {
    return existingLayer || null;
  } finally {
    restoringNodes.delete(node);
    resumeObserver();
  }
};

const flushPendingEnsure = () => {
  syncFrame = null;
  const nodes = Array.from(pendingEnsureNodes);
  pendingEnsureNodes.clear();
  nodes.forEach(ensureWatermark);
};

const scheduleEnsure = node => {
  if (typeof window === 'undefined' || !node) {
    return;
  }

  pendingEnsureNodes.add(node);

  if (syncFrame) {
    return;
  }

  syncFrame = window.requestAnimationFrame(flushPendingEnsure);
};

const scheduleSyncAll = () => {
  if (typeof window === 'undefined') {
    return;
  }

  registeredContainers.forEach(node => {
    pendingEnsureNodes.add(node);
  });

  if (syncFrame) {
    return;
  }

  syncFrame = window.requestAnimationFrame(flushPendingEnsure);
};

const getContainerFromMutation = mutation => {
  const { target, type } = mutation;

  if (!(target instanceof Element)) {
    return null;
  }

  if (type === 'attributes') {
    if (target.matches(`[${TABLE_WATERMARK_LAYER_ATTR}]`)) {
      return (
        (target.parentElement &&
          target.parentElement.closest(`[${TABLE_WATERMARK_ATTR}]`)) ||
        null
      );
    }

    if (target.matches(`[${TABLE_WATERMARK_LOGO_ATTR}]`)) {
      return target.closest(`[${TABLE_WATERMARK_ATTR}]`);
    }

    if (target.matches(`[${TABLE_WATERMARK_ATTR}]`)) {
      return target;
    }

    return null;
  }

  if (type === 'childList') {
    const hasRemovedWatermarkNode = Array.from(mutation.removedNodes).some(
      node =>
        node instanceof Element &&
        (node.hasAttribute(TABLE_WATERMARK_LAYER_ATTR) ||
          node.hasAttribute(TABLE_WATERMARK_LOGO_ATTR))
    );

    if (hasRemovedWatermarkNode) {
      return target.closest(`[${TABLE_WATERMARK_ATTR}]`);
    }
  }

  return null;
};

const handleMutations = mutations => {
  const affected = new Set();

  mutations.forEach(mutation => {
    const container = getContainerFromMutation(mutation);

    if (container && registeredContainers.has(container)) {
      affected.add(container);
    }
  });

  affected.forEach(scheduleEnsure);
};

const startSharedListeners = () => {
  if (typeof window === 'undefined' || listenersStarted) {
    return;
  }

  listenersStarted = true;

  observerOptions = {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: [
      'style',
      'src',
      TABLE_WATERMARK_ATTR,
      TABLE_WATERMARK_OFFSET_ATTR,
      TABLE_WATERMARK_LOGO_ATTR,
      TABLE_WATERMARK_LAYER_ATTR,
      'data-table-watermark-left',
      'data-table-watermark-left-mobile',
    ],
  };

  observer = new MutationObserver(handleMutations);

  mediaQueryList = window.matchMedia(TABLE_WATERMARK_MOBILE_MEDIA);

  if (mediaQueryList.addEventListener) {
    mediaQueryList.addEventListener('change', scheduleSyncAll);
  } else if (mediaQueryList.addListener) {
    mediaQueryList.addListener(scheduleSyncAll);
  }
};

const stopSharedListeners = () => {
  if (observer) {
    observer.disconnect();
    observer = null;
  }

  observerOptions = null;
  listenersStarted = false;

  if (mediaQueryList) {
    if (mediaQueryList.removeEventListener) {
      mediaQueryList.removeEventListener('change', scheduleSyncAll);
    } else if (mediaQueryList.removeListener) {
      mediaQueryList.removeListener(scheduleSyncAll);
    }

    mediaQueryList = null;
  }

  if (syncFrame && typeof window !== 'undefined') {
    window.cancelAnimationFrame(syncFrame);
    syncFrame = null;
  }

  pendingEnsureNodes.clear();
};

const observeContainerSize = node => {
  if (typeof ResizeObserver === 'undefined') {
    return;
  }

  const existing = resizeObservers.get(node);

  if (existing) {
    return;
  }

  const resizeObserver = new ResizeObserver(() => {
    if (registeredContainers.has(node)) {
      scheduleEnsure(node);
    }
  });

  resizeObserver.observe(node);
  resizeObservers.set(node, resizeObserver);
};

const unobserveContainerSize = node => {
  const resizeObserver = resizeObservers.get(node);

  if (!resizeObserver) {
    return;
  }

  resizeObserver.disconnect();
  resizeObservers.delete(node);
};

const observeContainerMutations = node => {
  if (!observer || !observerOptions || !node) {
    return;
  }

  observer.observe(node, observerOptions);
};

export const registerWatermarkContainer = (node, config = null) => {
  if (!TABLE_WATERMARK_ENABLED || !node) {
    return;
  }

  if (config) {
    setWatermarkContainerConfig(node, config);
  }

  const alreadyRegistered = registeredContainers.has(node);

  registeredContainers.add(node);
  startSharedListeners();
  observeContainerSize(node);

  if (!alreadyRegistered) {
    observeContainerMutations(node);
    // First paint: sync immediately so logos exist before browser paint.
    ensureWatermark(node);
  } else {
    scheduleEnsure(node);
  }
};

export const updateWatermarkContainerConfig = (node, config) => {
  if (!TABLE_WATERMARK_ENABLED || !node || !config) {
    return;
  }

  setWatermarkContainerConfig(node, config);

  if (registeredContainers.has(node)) {
    scheduleEnsure(node);
  }
};

export const unregisterWatermarkContainer = node => {
  if (!node) {
    return;
  }

  registeredContainers.delete(node);
  clearWatermarkContainerConfig(node);
  unobserveContainerSize(node);
  pendingEnsureNodes.delete(node);

  // Re-bind observer to remaining containers only.
  pauseObserver();
  resumeObserver();

  if (!registeredContainers.size) {
    stopSharedListeners();
  }
};

export const resyncAllWatermarkContainers = () => {
  if (!TABLE_WATERMARK_ENABLED) {
    return;
  }

  scheduleSyncAll();
};
